import {Quantiles, STANDARD_QUANTILES} from './quantile.ts';
import type {AccountHistory} from './account_history.ts';
import type {FlowHistory} from './flow_history.ts';
import type {SingleHistory} from './single_retirement_optimisation.ts';
import type {Assets} from './assets.ts';
import {
  get_default_flow_strategies,
  type Flow,
  type FlowStrategy,
} from './flow.ts';
// import type {StaticMarketData} from './market.ts';
// import type {Person} from './person.ts';
import {RetirementState} from './retirement_state.ts';
import type {RetirementStrategy} from './retirement_strategy.ts';
// import {
//   MIN_TARGET_NET_INCOME,
//   MAX_TARGET_NET_INCOME,
//   TARGET_NET_INCOME_INTERVAL,
// } from './settings.ts';
import {TimeSeries, TimeSeriesS} from './history.ts';
// import {zip_equal_sized_arrays} from './algo.ts';
import * as a_history from './account_history.ts';
import * as f_history from './flow_history.ts';

// const FAR_IN_THE_FUTURE = new Date(3000, 1, 1);

export function simulate_year(
  state: RetirementState,
  flow_strategies: FlowStrategy[],
  retirement_strategy: RetirementStrategy,
  noisy_logging: boolean,
): RetirementState {
  // Naive strategy to begin with.
  // TODO: if you don't succeed, try again in a different order

  const causal_flows: Flow[] = [];

  for (const s of flow_strategies) {
    const flows: Flow[] = s.compute_flows(
      state,
      retirement_strategy,
      noisy_logging,
    );

    for (const f of flows) {
      causal_flows.push(f);

      if (noisy_logging) {
        console.log(`flow_strategy: ${s.name} flow: ${f.toString()}`);
      }

      const assets = (f.amount > 0)
        ? state.assets.transfer(f.source, f.dest, f.amount)
        : state.assets.transfer(f.dest, f.source, -f.amount);

      state = state.setAssets(assets);
    }
  }

  return new RetirementState(
    state.year + 1,
    state.person,
    state.assets,
    state.market,
    causal_flows,
  );
}

export function simulate_retirement(
  note: string | undefined,
  initial_state: RetirementState,
  flow_strategies: FlowStrategy[],
  retirement_strategy: RetirementStrategy,
): number {
  // Simulate retirement from initial state until running out of money.
  // Returns: year when the money runs out.

  if (note !== undefined && note !== '') {
    console.log(
      `Starting ${initial_state.person.name}'s retirement sim: `
      + `retirement age=${initial_state.person.retirement_age}, ${note}`);
  }

  const singleHistory = simulate_single_history(
    initial_state,
    retirement_strategy,
    flow_strategies,
  );

  return singleHistory.exhaustion_year;
}

export function simulate_single_history(
  initial_state: RetirementState,
  retirement_strategy: RetirementStrategy,
  flow_strategies: FlowStrategy[],
): SingleHistory {
  const desired_net_income = retirement_strategy.desired_net_retirement_income;

  const fs = flow_strategies.length === 0
    ? get_default_flow_strategies(desired_net_income)
    : flow_strategies;

  const assets_map = new Map<number, Assets>();
  const flow_history: FlowHistory = [];

  try {
    let [year, assets, person] = [
      initial_state.year, initial_state.assets, initial_state.person,
    ];

    while (year < person.max_year) {  // upper limit of simulation
      year = initial_state.year;
      assets = initial_state.assets;
      assets_map.set(year, assets);
      const flows = initial_state.causal_flows;

      flow_history.push({
        year,
        flows,
      });

      const one_year_later = simulate_year(
        initial_state,
        fs,
        retirement_strategy,
        false,
      );

      initial_state = one_year_later;
    }
  } catch (ex) {
    if (typeof ex === 'string' && !ex.includes('Cannot achieve target net income')) {
      console.log('in single history simulation, we caught', ex);
    }
  }

  const unpaddedAssetHistory: AccountHistory = [...assets_map.entries()]
    .map(e => ({
      year: e[0],
      assets: e[1],
    }));

  const paddedAssetHistory = a_history.padToEndYear(
    unpaddedAssetHistory, initial_state.person.max_year);

  const assetTimeSeries: TimeSeriesS<number> = a_history.toTimeSeries(
    paddedAssetHistory);

  const flowTimeSeries: TimeSeriesS<number> = f_history.toTimeSeries(
    flow_history);

  const endYear: number = unpaddedAssetHistory.at(-1)!.year;
  const maxYear: number = initial_state.person.max_year;
  const exhausted: boolean = endYear < maxYear;
  const exhaustionYear: number = exhausted ? endYear : -1;

  return {
    asset_history: assetTimeSeries,
    flow_history: flowTimeSeries,
    exhausted,
    exhaustion_year: exhaustionYear,
    max_year: maxYear,
  };
}

export class MultipleHistory {
  static deserialize(s: string): MultipleHistory {
    const j = JSON.parse(s) as {
      maxYear: number;
      exhaustionYears: Quantiles;
      multipleAssetHistory: string;
    };

    return new MultipleHistory(
      j.maxYear,
      j.exhaustionYears,
      TimeSeriesS.deserialize<Quantiles>(
        j.multipleAssetHistory,
        Quantiles.deserialize,
      ),
    );
  }

  _maxYear: number;  // how far out to plot stuff
  _exhaustionYears: Quantiles;
  _multipleAssetHistory: TimeSeriesS<Quantiles>;

  constructor(
    maxYear: number,
    exhaustionYears: Quantiles,
    multipleAssetHistory: TimeSeriesS<Quantiles>,
  ) {
    this._maxYear = maxYear;
    this._exhaustionYears = exhaustionYears;
    this._multipleAssetHistory = multipleAssetHistory;
  }

  get maxYear(): number {
    return this._maxYear;
  }

  get exhaustionYears(): Quantiles {
    return this._exhaustionYears;
  }

  get histories(): TimeSeriesS<Quantiles> {
    return this._multipleAssetHistory;
  }

  serialize(): string {
    return JSON.stringify({
      maxYear: this._maxYear,
      exhaustionYear: this._exhaustionYears,
      multipleAssetHistory: this._multipleAssetHistory.serialize(
        q => q.serialize()),
    });
  }
}

export function simulate_multiple_histories(
  initial_state: RetirementState,
  retirement_strategy: RetirementStrategy,
  flow_strategies: FlowStrategy[],
  progressListener: (progress: number) => void,
): MultipleHistory {
  const numberOfHistories = 2500;
  const progressMessageInterval = 100;

  /* eslint @typescript-eslint/require-array-sort-compare: "off" */
  const histories: SingleHistory[] = [...Array.from({length: numberOfHistories}).keys()]
    .map(
      i => {
        const history = simulate_single_history(
          initial_state,
          retirement_strategy,
          flow_strategies,
        );

        if (i % progressMessageInterval === 0) {
          const completionFraction = i / numberOfHistories;
          progressListener(completionFraction);
        }

        return history;
      },
    ).sort();

  const exhaustionYearsAll = histories.map(h => h.exhaustion_year).sort();
  const exhaustionYears = new Quantiles(STANDARD_QUANTILES, exhaustionYearsAll);

  const years = new Set<number>();
  const names = new Set<string>();

  type ValueKey = {
    name: string;
    year: number;
  };

  function makeKeyString(key: ValueKey): string {
    return `${key.name}#${key.year}`;
  }

  const accumulator = new Map<string, number[]>();

  for (const h of histories) {
    for (const a of h.asset_history.data) {
      names.add(a.name);

      for (const d of a.data) {
        years.add(d.year);
        const key = makeKeyString({name: a.name, year: d.year});
        const values = (accumulator.get(key) ?? []);
        values.push(d.value);
        accumulator.set(key, values);
      }
    }
  }

  const multiple_asset_history = new TimeSeriesS<Quantiles>(
    [...names.keys()]
      .sort()
      .map(name => {
        const cells = [...years.keys()]
          .sort()
          .map(
            year => {
              const key = makeKeyString({name, year});
              const numbers = accumulator.get(key) ?? [];
              const q = new Quantiles(STANDARD_QUANTILES, numbers);

              return {
                year,
                value: q,
              };
            },
          );

        return new TimeSeries<Quantiles>(name, cells);
      }),
  );

  const maxYear = histories[0].max_year;

  return new MultipleHistory(
    maxYear,
    exhaustionYears,
    multiple_asset_history,
  );
}
